// src/Login.js
import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Form } from 'react-bootstrap';
import { useAuth } from './contexts/AuthContext';
import { Navigate, useSearchParams } from 'react-router-dom';
import './Login.css';

function Login() {
  const { signInWithAzure, signInWithEmail, isAuthenticated, loading, tenantError, clearTenantError } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    // Check if user was redirected after setting password
    if (searchParams.get('passwordSet') === 'true') {
      setSuccessMessage('Password set successfully! You can now sign in with your email and password.');
    }
  }, [searchParams]);

  // Display tenant authorization error
  useEffect(() => {
    if (tenantError) {
      setError(tenantError);
      clearTenantError();
    }
  }, [tenantError, clearTenantError]);

  // If already authenticated, redirect to home
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="login-page">
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <div className="text-white">Loading...</div>
        </Container>
      </div>
    );
  }

  const handleAzureSignIn = async () => {
    try {
      setError('');
      setSigningIn(true);
      console.log('Starting Azure sign in...');
      await signInWithAzure();
      console.log('Sign in initiated successfully');
      // User will be redirected to Azure login
    } catch (err) {
      console.error('Sign in error:', err);
      setError(`Failed to sign in: ${err.message || 'Please try again.'}`);
      setSigningIn(false);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSigningIn(true);
      await signInWithEmail(email, password);
      // Will redirect on successful login
    } catch (err) {
      console.error('Sign in error:', err);
      setError(err.message || 'Invalid email or password');
      setSigningIn(false);
    }
  };

  return (
    <div className="login-page">
      <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center mb-4">
          <h1 className="text-white mb-2">ROE Visual DB</h1>
          <p className="text-white-50">Production Barcode Checker</p>
        </div>

        <Card style={{ maxWidth: '500px', width: '100%' }} className="shadow-lg">
          <Card.Body className="p-4">
            <Card.Title className="text-center mb-4">
              <h2>Sign In</h2>
            </Card.Title>

            {successMessage && (
              <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
                {successMessage}
              </Alert>
            )}

            {error && <Alert variant="danger">{error}</Alert>}

            <Form onSubmit={handleEmailSignIn}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={signingIn}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={signingIn}
                  required
                />
              </Form.Group>

              <div className="d-grid">
                <Button
                  variant="primary"
                  size="lg"
                  type="submit"
                  disabled={signingIn}
                >
                  {signingIn ? 'Signing in...' : 'Sign In'}
                </Button>
              </div>

              <div className="text-center mt-3">
                <a href="/forgot-password" className="text-decoration-none small">
                  Forgot password?
                </a>
              </div>
            </Form>

            <hr className="my-4" />

            <div className="d-grid">
              <Button
                variant="outline-primary"
                size="lg"
                onClick={handleAzureSignIn}
                disabled={signingIn}
              >
                Sign in with Microsoft
              </Button>
            </div>

            <div className="text-center mt-3 text-muted small">
              <p>For ROE Visual employees</p>
            </div>

            <hr className="my-4" />
            <div className="text-center text-muted small">
              <p>Don't have access? <a href="/request-access">Request Access</a></p>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default Login;
